import { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import "@/App.css";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import CheckIn from "./pages/CheckIn";
import SessionHistory from "./pages/SessionHistory";
import Profile from "./pages/Profile";
import AuthCallback from "./pages/AuthCallback";
import Subscription from "./pages/Subscription";
import About from "./pages/About";
import Terms from "./pages/Terms";
import Affirmations from "./pages/Affirmations";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import Admin from "./pages/Admin";
import Discover from "./pages/Discover";
import Store from "./pages/Store";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import StoreSuccess from "./pages/StoreSuccess";
import Orders from "./pages/Orders";
import OrderLookup from "./pages/OrderLookup";
import SavedPrompts from "./pages/SavedPrompts";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";

// Components
import { Toaster } from "./components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth context
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (e) {
      console.error('Logout error:', e);
    }
    setUser(null);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return { user, loading, login, logout, checkAuth };
};

// Protected Route component
const ProtectedRoute = ({ children, user, loading }) => {
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground font-light">Taking a breath...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();
  const auth = useAuth();
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback auth={auth} />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing user={auth.user} />} />
      <Route path="/login" element={<Login auth={auth} />} />
      <Route path="/register" element={<Register auth={auth} />} />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute user={auth.user} loading={auth.loading}>
            <Dashboard auth={auth} />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/check-in" 
        element={
          <ProtectedRoute user={auth.user} loading={auth.loading}>
            <CheckIn auth={auth} />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/history" 
        element={
          <ProtectedRoute user={auth.user} loading={auth.loading}>
            <SessionHistory auth={auth} />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute user={auth.user} loading={auth.loading}>
            <Profile auth={auth} />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/subscription" 
        element={
          <ProtectedRoute user={auth.user} loading={auth.loading}>
            <Subscription auth={auth} />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/subscription/success" 
        element={
          <ProtectedRoute user={auth.user} loading={auth.loading}>
            <Subscription auth={auth} />
          </ProtectedRoute>
        } 
      />
      <Route path="/about" element={<About />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/discover" element={<Discover />} />
      
      {/* Store Routes - Public access for guest shopping */}
      <Route path="/store" element={<Store auth={auth} />} />
      <Route path="/store/success" element={<StoreSuccess auth={auth} />} />
      <Route path="/store/:productId" element={<ProductDetail auth={auth} />} />
      <Route path="/cart" element={<Cart auth={auth} />} />
      <Route path="/track-order" element={<OrderLookup />} />
      <Route path="/orders" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <Orders auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/saved-prompts" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <SavedPrompts auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/blog" element={<Blog auth={auth} />} />
      <Route path="/blog/:slug" element={<BlogPost auth={auth} />} />
      <Route 
        path="/affirmations" 
        element={
          <ProtectedRoute user={auth.user} loading={auth.loading}>
            <Affirmations auth={auth} />
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-background">
      <BrowserRouter>
        <AppRouter />
        <Toaster position="bottom-center" />
      </BrowserRouter>
    </div>
  );
}

export default App;
